<script lang="ts">
  /**
   * F17 — Vault answers questions, not just stores files.
   *
   * "When does the car licence expire?" comes back as a sentence with **the source
   * document beside it**. Two rules run this screen, and both are visible in the
   * markup rather than left to the API:
   *
   *  · **Never an answer without its receipt.** The answer and the citation render
   *    in one block. There is no branch that prints a sentence with nothing beside
   *    it — if `cite` is null the sentence is not shown at all, whatever the API
   *    sent.
   *  · **Not knowing is a real answer.** "Vault does not know" with the reason in
   *    words beats a plausible sentence assembled from inference, and it is the
   *    outcome that tells the household what to fix.
   *
   * **No percentage appears anywhere on this screen**, by design. A number nobody
   * can interpret invites an argument about the number instead of the document.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { expiryOf, pretty, type Answer } from "../lib/model";

  const {
    onopen,
    onquery,
  }: {
    onopen: (id: string) => void;
    /** The same text, handed to the list so browsing and asking are one box. */
    onquery: (q: string) => void;
  } = $props();

  let q = $state("");
  let busy = $state(false);
  let result = $state<Answer | null>(null);
  let error = $state<string | null>(null);

  async function submit() {
    const question = q.trim();
    onquery(question);
    if (!question) {
      result = null;
      error = null;
      return;
    }
    busy = true;
    error = null;
    try {
      result = await api.post<Answer>("/ask", { q: question });
    } catch (e) {
      // A failure is a failure, never a shrugged "does not know" — the two mean
      // completely different things and only one of them is Vault's fault.
      result = null;
      error = e instanceof ApiError ? e.message : "Could not reach Vault to ask that.";
    } finally {
      busy = false;
    }
  }

  function clear() {
    q = "";
    result = null;
    error = null;
    onquery("");
  }
</script>

<form
  class="ask"
  onsubmit={(e) => {
    e.preventDefault();
    void submit();
  }}
>
  <Icon name="search" size={16} />
  <input
    bind:value={q}
    aria-label="Ask Vault a question, or search for a document"
    placeholder="When does the car licence expire?"
  />
  {#if q}
    <button type="button" class="ask__x" onclick={clear} aria-label="Clear">
      <Icon name="x" size={14} />
    </button>
  {/if}
  <button type="submit" class="btn-primary" disabled={busy}>{busy ? "Asking…" : "Ask"}</button>
</form>

{#if error}
  <div class="attn attn--warn" role="alert">
    <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
    <span class="attn__text">{error} This is a failure to ask, not an answer of “no”.</span>
  </div>
{:else if result}
  <section class="ans" class:ans--unknown={!result.answer}>
    <div class="ans__body">
      {#if result.answer && result.cite}
        <!-- Both conditions, deliberately. The API guarantees it; the screen does
             not take that on trust, because this is the one property the whole
             feature is worth having for. -->
        <p class="ans__t">{result.answer}</p>
        <p class="ans__k">
          <Icon name="info" size={12} />
          Read off the document beside this. Vault answers from what is filed, never from inference.
        </p>
      {:else}
        <p class="ans__t ans__t--unknown">
          <Icon name="alert" size={16} />
          Vault does not know.
        </p>
        <p class="ans__k">{result.because}</p>
      {/if}

      {#if result.alsoSeen.length > 0}
        <div class="ans__also">
          <span class="ans__alsoLabel">Also matched</span>
          {#each result.alsoSeen as a (a.id)}
            <button class="lnk" onclick={() => onopen(a.id)}>{a.title}</button>
          {/each}
        </div>
      {/if}
    </div>

    {#if result.cite}
      {@const c = result.cite}
      {@const exp = expiryOf(c.expiresAt)}
      <!-- The receipt. Beside the answer, never underneath a fold. -->
      <button class="rcpt" onclick={() => onopen(c.id)}>
        <span class="rcpt__kicker">
          <Icon name={c.inCase ? "shield" : "file"} size={12} />
          {result.answer ? "The source" : "Closest document"}
        </span>
        <span class="rcpt__t">{c.title}</span>
        <span class="rcpt__s">
          {[pretty(c.docType), c.supplier, c.documentDate ? dateZA(c.documentDate) : null]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {#if exp}
          <span class="rcpt__exp" class:rcpt__exp--warn={exp.tone === "warn"}>
            {#if exp.glyph}<Icon name={exp.glyph} size={11} />{/if}
            {exp.word}
          </span>
        {/if}
        <span class="rcpt__go">
          {c.hasFile ? "Open the document" : "No file — record only"}
          <Icon name="chevron-right" size={12} />
        </span>
      </button>
    {/if}
  </section>
{/if}

<style>
  .ask {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    padding: 7px 7px 7px 14px;
    border-radius: var(--r-pill);
    background: var(--fill);
    box-shadow: inset 0 0 0 1px var(--line);
    color: var(--mut);
  }
  .ask:focus-within {
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .ask input {
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    outline: none;
    padding: 0;
    font-size: var(--fs-body);
    color: var(--tx);
  }
  .ask__x {
    color: var(--mut);
    padding: 4px;
  }
  .ask__x:hover {
    color: var(--acc);
  }

  .ans {
    display: flex;
    gap: var(--sp-4);
    align-items: stretch;
    margin-top: var(--sp-3);
    padding: var(--sp-4);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  /* Amber rule down the edge when Vault could not answer — with the words "Vault
     does not know" right beside it, so the colour is never carrying it alone. */
  .ans--unknown {
    border-left: 3px solid var(--warn);
  }
  .ans__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }
  .ans__t {
    margin: 0;
    font-family: var(--font-heading);
    font-size: var(--fs-h3);
    font-weight: 600;
    line-height: 1.35;
    color: var(--tx);
  }
  .ans__t--unknown {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    color: var(--warn);
  }
  .ans__k {
    margin: 0;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: var(--fs-small);
    color: var(--mut);
    line-height: 1.5;
  }
  .ans__also {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    flex-wrap: wrap;
    margin-top: auto;
    padding-top: var(--sp-2);
  }
  .ans__alsoLabel {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .lnk {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--tx2);
  }
  .lnk:hover {
    color: var(--acc);
  }

  .rcpt {
    flex: 0 0 clamp(220px, 32%, 320px);
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: var(--sp-3);
    text-align: left;
    border-radius: var(--r-control);
    background: var(--s2);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .rcpt:hover {
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .rcpt__kicker {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: var(--fs-micro);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--acc);
  }
  .rcpt__t {
    font-family: var(--font-heading);
    font-size: var(--fs-lead);
    font-weight: 600;
    line-height: 1.25;
    color: var(--tx);
  }
  .rcpt__s {
    font-size: var(--fs-small);
    color: var(--mut);
  }
  .rcpt__exp {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .rcpt__exp--warn {
    color: var(--warn);
  }
  .rcpt__go {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: var(--sp-2);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--tx2);
  }

  @container panel (max-width: 560px) {
    .ans {
      flex-direction: column;
    }
    .rcpt {
      flex: 1 1 auto;
    }
  }
</style>
